import React, { useState, useEffect } from 'react';
import { 
  useSections, 
  toggleSectionVisibility, 
  reorderSections, 
  createSection, 
  deleteSection 
} from '../hooks/useSections';
import { Eye, EyeOff, GripVertical, Edit, Trash2, Plus, Save } from 'lucide-react';
import SectionEditModal from './SectionEditModal';

const SectionManager = () => {
  const { sections, loading, error, refetch } = useSections(false);
  const [localSections, setLocalSections] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [showNewSectionForm, setShowNewSectionForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [newSectionData, setNewSectionData] = useState({
    name: '',
    type: '',
    is_visible: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (sections) {
      setLocalSections([...sections].sort((a, b) => a.display_order - b.display_order));
    }
  }, [sections]);

  const handleToggleVisibility = async (sectionId, currentVisibility) => {
    try {
      await toggleSectionVisibility(sectionId, !currentVisibility);
      refetch();
    } catch (err) {
      alert(`Failed to toggle visibility: ${err.message}`);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newSections = [...localSections];
    const draggedItem = newSections[draggedIndex];
    
    // Remove from old position
    newSections.splice(draggedIndex, 1);
    // Insert at new position
    newSections.splice(index, 0, draggedItem);
    
    setLocalSections(newSections);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;
    
    // Update display_order for all sections
    const reorderedSections = localSections.map((section, index) => ({
      id: section.id,
      display_order: index + 1
    }));
    
    try {
      setSaving(true);
      await reorderSections(reorderedSections);
      await refetch();
    } catch (err) {
      alert(`Failed to reorder sections: ${err.message}`);
    } finally {
      setSaving(false);
      setDraggedIndex(null);
    }
  };

  const handleCreateSection = async (e) => {
    e.preventDefault();
    
    if (!newSectionData.name || !newSectionData.type) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      await createSection(newSectionData);
      setShowNewSectionForm(false);
      setNewSectionData({ name: '', type: '', is_visible: true });
      await refetch();
    } catch (err) {
      alert(`Failed to create section: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSection = async (sectionId, sectionName) => {
    if (!window.confirm(`Are you sure you want to delete "${sectionName}"?`)) {
      return;
    }

    try {
      await deleteSection(sectionId);
      await refetch();
    } catch (err) {
      alert(`Failed to delete section: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ marginTop: '1rem' }}>Loading sections...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Error loading sections: {error}
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', background: 'white', borderRadius: '12px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--primary)' }}>Landing Page Sections</h2>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
            Manage visibility, order, and content of landing page sections
          </p>
        </div>
        <button
          onClick={() => setShowNewSectionForm(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: 'var(--accent)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 600
          }}
        >
          <Plus size={20} />
          Add Section
        </button>
      </div>

      {/* New Section Form */}
      {showNewSectionForm && (
        <div style={{
          background: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          border: '2px solid var(--accent)'
        }}>
          <h3 style={{ marginBottom: '1rem' }}>Create New Section</h3>
          <form onSubmit={handleCreateSection}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Section Name *
                </label>
                <input
                  type="text"
                  value={newSectionData.name}
                  onChange={(e) => setNewSectionData({ ...newSectionData, name: e.target.value })}
                  placeholder="e.g., Success Stories"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Section Type *
                </label>
                <input
                  type="text"
                  value={newSectionData.type}
                  onChange={(e) => setNewSectionData({ ...newSectionData, type: e.target.value })}
                  placeholder="e.g., success-stories"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                  required
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                {saving ? 'Creating...' : 'Create Section'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewSectionForm(false);
                  setNewSectionData({ name: '', type: '', is_visible: true });
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sections List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {localSections.map((section, index) => (
          <div
            key={section.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1.5rem',
              background: section.is_visible ? 'white' : '#f8f9fa',
              border: `2px solid ${section.is_visible ? 'var(--primary)' : '#dee2e6'}`,
              borderRadius: '8px',
              cursor: 'move',
              transition: 'all 0.2s',
              opacity: draggedIndex === index ? 0.5 : 1
            }}
          >
            {/* Drag Handle */}
            <div style={{ cursor: 'grab', color: '#6c757d' }}>
              <GripVertical size={24} />
            </div>

            {/* Order Number */}
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'var(--accent)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '1.2rem'
            }}>
              {index + 1}
            </div>

            {/* Section Info */}
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: 0, color: 'var(--primary)', fontWeight: 600 }}>
                {section.name}
              </h4>
              <p style={{ margin: '0.25rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                Type: <code>{section.type}</code>
              </p>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {/* Visibility Toggle */}
              <button
                onClick={() => handleToggleVisibility(section.id, section.is_visible)}
                style={{
                  padding: '0.5rem',
                  background: section.is_visible ? 'var(--success)' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  minWidth: '100px',
                  justifyContent: 'center'
                }}
                title={section.is_visible ? 'Hide section' : 'Show section'}
              >
                {section.is_visible ? (
                  <>
                    <Eye size={18} /> Visible
                  </>
                ) : (
                  <>
                    <EyeOff size={18} /> Hidden
                  </>
                )}
              </button>

              {/* Edit Button */}
              <button
                onClick={() => setEditingSection(section)}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                title="Edit section content"
              >
                <Edit size={18} /> Edit
              </button>

              {/* Delete Button */}
              <button
                onClick={() => handleDeleteSection(section.id, section.name)}
                style={{
                  padding: '0.5rem',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="Delete section"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {saving && (
        <div style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          padding: '1rem 1.5rem',
          background: 'var(--primary)',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <Save size={20} />
          Saving changes...
        </div>
      )}

      {/* Edit Modal */}
      <SectionEditModal
        section={editingSection}
        isOpen={!!editingSection}
        onClose={() => setEditingSection(null)}
        onSave={() => {
          refetch();
          setEditingSection(null);
        }}
      />
    </div>
  );
};

export default SectionManager;
